/**
 * Open-redirect-safe return-to resolution (plan §6.8.11).
 * Only same-origin absolute paths; external / protocol-relative → `/`.
 */

export interface ReturnToLocation {
  readonly pathname: string;
  readonly search: Readonly<Record<string, unknown>>;
}

const FALLBACK: ReturnToLocation = Object.freeze({ pathname: '/', search: {} });

/*
 * Browsers STRIP tab, LF and CR while parsing a URL, so a control character
 * defeats every prefix check below by sitting inside the token it would
 * otherwise match: '/\t/evil.example' does not start with '//' at parse time
 * here, but becomes protocol-relative — and off-origin — by the time it is
 * navigated. Rejecting the whole C0/C7F range closes that by construction
 * rather than by enumerating the three characters known to be stripped;
 * control characters have no legitimate place in a pathname.
 */
const UNIT_SEPARATOR = 0x1f;
const DELETE_CHAR = 0x7f;

/* A code-point scan rather than a regex: the equivalent character class trips
   `no-control-regex`, and suppressing a rule that exists to catch stray control
   characters — in the one function whose job is to reject them — would be the
   wrong trade for two lines. */
function hasControlChar(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code <= UNIT_SEPARATOR || code === DELETE_CHAR) return true;
  }
  return false;
}

function isSafePathname(pathname: string): boolean {
  if (pathname.length === 0) return false;
  if (!pathname.startsWith('/')) return false;
  if (hasControlChar(pathname)) return false;
  if (pathname.startsWith('//')) return false;
  if (pathname.includes('\\')) return false;
  if (pathname.includes('://')) return false;
  if (pathname.includes('..')) return false;
  return true;
}

export function resolveReturnTo(input: ReturnToLocation | null | undefined): ReturnToLocation {
  if (input === null || input === undefined) {
    return FALLBACK;
  }
  if (!isSafePathname(input.pathname)) {
    return FALLBACK;
  }
  return { pathname: input.pathname, search: cloneSearch(input.search) };
}

function cloneSearch(search: Readonly<Record<string, unknown>>): Readonly<Record<string, unknown>> {
  try {
    const parsed: unknown = JSON.parse(JSON.stringify(search));
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return parsed as Readonly<Record<string, unknown>>;
  } catch {
    return {};
  }
}

export function readReturnToState(state: unknown): ReturnToLocation {
  if (typeof state !== 'object' || state === null || !('returnTo' in state)) return FALLBACK;
  const returnTo = state.returnTo;
  if (typeof returnTo !== 'object' || returnTo === null) return FALLBACK;
  const candidate = returnTo as { pathname?: unknown; search?: unknown };
  if (typeof candidate.pathname !== 'string') return FALLBACK;
  if (
    typeof candidate.search !== 'object' ||
    candidate.search === null ||
    Array.isArray(candidate.search)
  ) {
    return FALLBACK;
  }
  return resolveReturnTo({
    pathname: candidate.pathname,
    search: candidate.search as Readonly<Record<string, unknown>>,
  });
}
